import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { ArrowLeft, Calendar, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (err) {
        setError("Post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <Navigation />
        <main className="pt-32 pb-24 px-6 sm:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-serif text-4xl text-[#1A202C] mb-4">
              Post not found
            </h1>
            <p className="font-sans text-[#4A5568] mb-8">
              The post you're looking for doesn't exist or has been removed.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 font-sans text-[#D4AF37] hover:text-[#B3932E]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-[#FAF9F6]">
      <Navigation />

      <main className="pt-32 pb-24 px-6 sm:px-12">
        <article className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link
            to="/blog"
            data-testid="back-to-blog-link"
            className="inline-flex items-center gap-2 font-sans text-sm text-[#718096] hover:text-[#4A5568] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-12">
            <h1
              data-testid="blog-post-title"
              className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1A202C] mb-6 leading-tight"
            >
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-[#718096]">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          {post.featured_image && (
            <div className="mb-12">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <div
            data-testid="blog-post-content"
            className="font-sans text-lg text-[#4A5568] leading-relaxed space-y-6"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {post.content}
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
